/**
 * Cliente do compartilhamento do laudo com revisores (E24.F4).
 *
 * `POST /casos/:id/laudo/share` notifica todos os revisores ativos do
 * escritório por e-mail e retorna o snapshot dos destinatários. Os códigos
 * de erro do backend são classificados em razões tipadas para a UI mapear
 * mensagens PT-BR via i18n.
 */
import axios, { type AxiosError, type AxiosInstance } from 'axios';

export interface LaudoShareResultData {
  recipients: string[];
  sent_at: string;
}

export type LaudoShareErrorReason =
  | 'no_reviewers'
  | 'not_ready'
  | 'not_configured'
  | 'send_failed'
  | 'case_not_found'
  | 'owner_required'
  | 'unknown';

export class LaudoShareError extends Error {
  public readonly reason: LaudoShareErrorReason;

  constructor(reason: LaudoShareErrorReason, message?: string) {
    super(message ?? reason);
    this.reason = reason;
    this.name = 'LaudoShareError';
  }
}

const HTTP_NOT_FOUND = 404;
const HTTP_FORBIDDEN = 403;
const HTTP_CONFLICT = 409;
const HTTP_UNPROCESSABLE = 422;
const HTTP_BAD_GATEWAY = 502;
const HTTP_SERVICE_UNAVAILABLE = 503;

const _STATUS_DETAIL_MAP: ReadonlyArray<{
  httpStatus: number;
  detail: string;
  reason: LaudoShareErrorReason;
}> = [
  { httpStatus: HTTP_CONFLICT, detail: 'NO_REVIEWERS', reason: 'no_reviewers' },
  { httpStatus: HTTP_UNPROCESSABLE, detail: 'CASE_LAUDO_NOT_READY', reason: 'not_ready' },
  {
    httpStatus: HTTP_SERVICE_UNAVAILABLE,
    detail: 'EMAIL_NOT_CONFIGURED',
    reason: 'not_configured',
  },
  { httpStatus: HTTP_BAD_GATEWAY, detail: 'EMAIL_SEND_FAILED', reason: 'send_failed' },
  { httpStatus: HTTP_NOT_FOUND, detail: 'CASE_NOT_FOUND', reason: 'case_not_found' },
  { httpStatus: HTTP_FORBIDDEN, detail: 'OWNER_REQUIRED', reason: 'owner_required' },
];

export function classifyLaudoShareError(err: unknown): LaudoShareError {
  if (!axios.isAxiosError(err)) return new LaudoShareError('unknown');
  const axiosErr = err as AxiosError;
  const httpStatus = axiosErr.response?.status ?? 0;
  const data = axiosErr.response?.data as { detail?: unknown } | undefined;
  const detail = typeof data?.detail === 'string' ? data.detail : '';
  const match = _STATUS_DETAIL_MAP.find((m) => m.httpStatus === httpStatus && m.detail === detail);
  return match ? new LaudoShareError(match.reason) : new LaudoShareError('unknown');
}

export async function shareLaudo(
  client: AxiosInstance,
  caseId: string,
): Promise<LaudoShareResultData> {
  try {
    const { data } = await client.post<LaudoShareResultData>(`/casos/${caseId}/laudo/share`);
    return data;
  } catch (err) {
    throw classifyLaudoShareError(err);
  }
}
